import { AddButton, Button, CloseButton } from '@ui/shared/Button';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import {
  closeInnerPopup,
  openInnerPopup,
} from '../../../data/providers/popupsProvider';
import { userProvider } from '../../../data/providers/userProvider';
import { IPermissionOption, ISelectOption, IUser } from '../../../data/types';
import { addNewUser } from '../../../data/writers/userManagement';
import { CardBody } from '../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../shared/card/CardHeader';
import { ErrorMessage } from '../../shared/form/ErrorMessage';
import ERRORS from '../../../constants/firebase-auth-errors';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import {
  InnerPopupCard,
  PopupCardContent,
} from '../../shared/popups/PopupCard';

import { UserForm } from './UserForm';

/**
 * @description button to open the add user popup
 */
export const AddUser = withRouter(
  (props: RouteComponentProps): React.ReactElement => {
    const openDialog = (): void =>
      openInnerPopup(
        <Popup onClose={closeInnerPopup(props.history)} />,
        props.history,
        'add'
      );

    return (
      <AddButton onClick={openDialog} />
    );
  }
);

interface IAddUserPopupProps {
  onClose: () => void;
}

/**
 * @description add api user popup
 * @param props
 */
const Popup = (props: IAddUserPopupProps): React.ReactElement => {
  const [user, setUser] = React.useState<IUser>();
  const [saving, setSaving] = React.useState(false);
  const [permission, setPermission] = React.useState<IPermissionOption>();
  const [email, setEmail] = React.useState('');
  const [name, setName] = React.useState('');
  const [error, setError] = React.useState('');
  const [team, setTeam] = React.useState<ISelectOption>();
  const [loading, setLoading] = React.useState<boolean>(true);

  React.useEffect((): (() => void) => {
    const subscriber = userProvider().subscribe((mUser: IUser): void => {
      setUser(mUser);
      setLoading(false);
    });
    return (): void => subscriber.unsubscribe();
  }, []);

  /**
   * @description saves the user
   */
  const saveUser = () => {
    setSaving(true);
    addNewUser(name.trim(), email.trim(), team.value, permission.label)
      .then(() => {
        setSaving(false);
        props.onClose();
      })
      .catch((err) => {
        setSaving(false);
        setError(`Error adding user:  ${ERRORS[err.code]}`);
      });
  };

  const isValid = (): boolean => {
    return (
      name.length > 0 &&
      email.length > 0 &&
      !!team?.value &&
      !!permission?.value
    );
  };

  return (
    <InnerPopupCard onClose={props.onClose} size='medium' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h5>Add New User</h5>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          {saving || loading ? (
            <LinearLoading />
          ) : (
            <UserForm
              requestingUser={user}
              name={name}
              setName={setName}
              email={email}
              setEmail={setEmail}
              team={team}
              setTeam={setTeam}
              permission={permission}
              setPermission={setPermission}
            />
          )}
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft>
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </CardFooterLeft>
          <CardFooterRight>
            <Button
              disabled={saving || !isValid()}
              onClick={saveUser}
              title='Add User'
              icon='person_add'
              style={{ whiteSpace: 'nowrap' }}
             />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </InnerPopupCard>
  );
};
